import { useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import CONFIG from './CONFIG';

const blobCache = new Map();

export function useCachedBlobUrl() {
  const { getAccessTokenSilently } = useAuth0();
  const { organizationId } = useParams();

  const getCachedBlobUrl = useCallback(
    async (key, type, opts = {}) => {
      const fullUrl = `${CONFIG.SERVER_CHANNEL}/v1/files/${key}?type=${type}`;

      if (blobCache.has(fullUrl)) return blobCache.get(fullUrl);

      try {
        const token = await getAccessTokenSilently();

        const response = await axios.get(fullUrl, {
          responseType: 'blob',
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Organization-Key': organizationId,
          },
          signal: opts.signal,
        });

        const blobUrl = URL.createObjectURL(response.data);
        blobCache.set(fullUrl, blobUrl);
        return blobUrl;
      } catch (err) {
        console.error('Error fetching blob:', err);
        return null;
      }
    },
    [getAccessTokenSilently, organizationId],
  );

  const getArtifactUrl = useCallback(
    async (key, type, opts = {}) => {
      const fullUrl = `${CONFIG.SERVER_ORGANIZATION}/v1/artifacts/file/${key}`;

      try {
        const token = await getAccessTokenSilently();

        const response = await axios.get(fullUrl, {
          responseType: 'blob',
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Organization-Key': organizationId,
          },
          signal: opts.signal,
        });

        return URL.createObjectURL(response.data);
      } catch (err) {
        console.error('Error fetching blob:', err);
        return null;
      }
    },
    [getAccessTokenSilently, organizationId],
  );

  const getPublicArtifactAddressUrl = useCallback(
    async (key, type, opts = {}) => `${CONFIG.SERVER_ORGANIZATION}/v1/artifacts/public_file/${key}`,
    [],
  );

  const getPublicArtifactUrl = useCallback(
    async (key, type, opts = {}) => {
      const fullUrl = `${CONFIG.SERVER_ORGANIZATION}/v1/artifacts/public_file/${key}`;

      try {
        const token = await getAccessTokenSilently();

        const response = await axios.get(fullUrl, {
          responseType: 'blob',
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Organization-Key': organizationId,
          },
          signal: opts.signal,
        });

        return URL.createObjectURL(response.data);
      } catch (err) {
        console.error('Error fetching blob:', err);
        return null;
      }
    },
    [getAccessTokenSilently, organizationId],
  );

  const getDocumentUrl = useCallback(
    async (key, type, opts = {}) => {
      const fullUrl = `${CONFIG.SERVER_DOCUMENT}/v1/documents/document/${key}`;

      try {
        const token = await getAccessTokenSilently();

        const response = await axios.get(fullUrl, {
          responseType: 'blob',
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Organization-Key': organizationId,
          },
          signal: opts.signal,
        });

        return URL.createObjectURL(response.data);
      } catch (err) {
        console.error('Error fetching blob:', err);
        return null;
      }
    },
    [getAccessTokenSilently, organizationId],
  );

  return {
    getCachedBlobUrl,
    getArtifactUrl,
    getPublicArtifactUrl,
    getDocumentUrl,
    getPublicArtifactAddressUrl,
  };
}
